import { clsx } from 'clsx';
import { useEffect } from 'react';
import { trackPackViewed } from '@/lib/analytics';

interface ClaimPack {
  id: string;
  packType: string;
  fileUrl: string;
  generatedAt: string;
  version: number;
}

interface PackDownloadListProps {
  packs: ClaimPack[];
  isGenerating?: boolean;
  dedupeScope?: string;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PackDownloadList({ packs, isGenerating, dedupeScope }: PackDownloadListProps) {
  useEffect(() => {
    for (const packType of new Set(packs.map((pack) => pack.packType))) {
      if (packType === 'council' || packType === 'court') {
        trackPackViewed(packType, dedupeScope);
      }
    }
  }, [packs, dedupeScope]);

  if (packs.length === 0 && !isGenerating) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6">
      <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
        Your Claim Packs
      </h3>

      {isGenerating && (
        <div className="flex items-center gap-3 p-4 bg-brand-50 border border-brand-200 rounded-xl mb-4">
          <div className="relative">
            <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
              <span className="text-lg animate-pulse">📄</span>
            </div>
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-500"></span>
            </span>
          </div>
          <div className="flex-1">
            <p className="font-medium text-brand-900">Generating your pack...</p>
            <p className="text-sm text-brand-600">
              This usually takes 1-2 minutes. You'll receive an email when it's ready.
            </p>
          </div>
        </div>
      )}

      {packs.length > 0 && (
        <div className="space-y-3">
          {packs.map((pack) => (
            <div
              key={pack.id}
              className="flex items-center justify-between p-4 bg-surface-50 rounded-xl border border-surface-200 hover:border-brand-300 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className={clsx(
                  "w-12 h-12 rounded-xl flex items-center justify-center",
                  pack.packType === 'council' 
                    ? "bg-brand-100" 
                    : "bg-amber-100"
                )}>
                  <span className="text-2xl">
                    {pack.packType === 'council' ? '📋' : '⚖️'}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-brand-950">
                    {pack.packType === 'council' ? 'Council' : 'Court'} Claim Pack
                  </p>
                  <p className="text-sm text-surface-600">
                    Generated {formatDate(pack.generatedAt)}
                    {pack.version > 1 && ` (v${pack.version})`}
                  </p>
                </div>
              </div>
              
              <a
                href={pack.fileUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(
                  "flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors",
                  "bg-brand-600 text-white hover:bg-brand-700",
                  "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                )}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </a>
            </div>
          ))}
        </div>
      )}

      {packs.length > 0 && (
        <div className="mt-4 pt-4 border-t border-surface-100">
          <p className="text-xs text-surface-500">
            💡 <strong>Tip:</strong> Keep a copy of your claim pack for your records before submitting to the council.
          </p>
        </div>
      )}
    </div>
  );
}
